import type { Object3D } from 'three';
import { isAbstractSketchEdgeObject } from '../../../../mesh/guards/isAbstractSketchEdgeObject';
import type { AbstractSketchEdgeObject } from '../../../../mesh/sketch/AbstractSketchEdgeObject';
import type { SketchPointObject } from '../../../../mesh/sketch/SketchPointObject';
import { findAncestorSketchGroupPersistentId } from '../../../../manager/SketchEditingInteractionGuard';
import { resolveSourceDisplayLabel } from '../../model/EdgeLabelUtils';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../../model/SketchEntityReferenceDiagnostics';
import type { SketchEntityReferenceSelectionCandidateSource } from './SketchEntityReferenceSelectionAdapter';
import type {
	SketchCurveChainExpansionInput,
	SketchCurveChainExpansionResult,
	ChainWalkResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchCurveChainExpansionService';

export type {
	SketchCurveChainExpansionInput,
	SketchCurveChainExpansionResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchCurveChainExpansionService';

/**
 * 外部草图曲线选择链扩展服务。
 *
 * 说明：
 * 1. SolidWorks Convert Entities 的 Select chain 只作用于连续草图实体；
 * 2. 本服务只沿共享端点扩展，不跨草图、不处理 topology edge loop；
 * 3. 遇到分支时停止当前方向并返回 warning，避免猜测用户意图。
 */
export class SketchCurveChainExpansionService {
	/**
	 * 展开外部草图曲线选择链。
	 *
	 * @param input 链扩展输入。
	 * @returns 链扩展结果。
	 */
	public expand(input: SketchCurveChainExpansionInput): SketchCurveChainExpansionResult {
		if (input.sourceSketchId === input.targetSketchId) {
			return {
				sources: [],
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'selection-chain-target-sketch-rejected',
						level: 'error',
						message: '选择链不能引用当前目标草图内的实体。',
					}),
				],
			};
		}

		const startEdge = this.resolveSupportedExternalEdge(input.startObject, input.sourceSketchId);
		if (!startEdge) {
			return {
				sources: [],
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'unsupported-source',
						level: 'error',
						message: '当前对象不是可用于选择链的外部草图曲线。',
					}),
				],
			};
		}

		const endpoints = this.resolveEndpointPoints(startEdge);
		if (endpoints.length < 2) {
			return {
				sources: [this.createSource(input.sourceSketchId, startEdge)],
				diagnostics: [],
			};
		}

		const visitedEdges = new Set<AbstractSketchEdgeObject>([startEdge]);
		const backward = this.walkFromEndpoint({
			sourceSketchId: input.sourceSketchId,
			previousEdge: startEdge,
			currentEndpoint: endpoints[0]!,
			visitedEdges,
		});
		const forward = this.walkFromEndpoint({
			sourceSketchId: input.sourceSketchId,
			previousEdge: startEdge,
			currentEndpoint: endpoints[1]!,
			visitedEdges,
		});
		const orderedEdges = [
			...backward.edges.reverse(),
			startEdge,
			...forward.edges,
		];

		return {
			sources: orderedEdges.map((edge) => this.createSource(input.sourceSketchId, edge)),
			diagnostics: [
				...backward.diagnostics,
				...forward.diagnostics,
			],
		};
	}

	/**
	 * 从端点沿一个方向扩展链。
	 *
	 * @param input 单向扩展输入。
	 * @returns 单向扩展结果。
	 */
	private walkFromEndpoint(input: {
		/** 来源草图 ID。*/
		sourceSketchId: string;
		/** 上一条已经进入链的边。*/
		previousEdge: AbstractSketchEdgeObject;
		/** 当前需要继续查找的共享端点。*/
		currentEndpoint: SketchPointObject;
		/** 全链已访问边集合。*/
		visitedEdges: Set<AbstractSketchEdgeObject>;
	}): ChainWalkResult {
		const edges: AbstractSketchEdgeObject[] = [];
		const diagnostics: ExecutionDiagnostic[] = [];
		let previousEdge = input.previousEdge;
		let currentEndpoint: SketchPointObject | null = input.currentEndpoint;

		while (currentEndpoint) {
			const connectedEdges = this.resolveConnectedChainEdges(currentEndpoint, input.sourceSketchId);
			if (connectedEdges.length > 2) {
				diagnostics.push(createExecutionDiagnostic({
					kind: 'selection-chain-branch-stopped',
					level: 'warning',
					message: '选择链遇到分支端点，已停止扩展该方向。',
					sourceRefEntityId: previousEdge.persistentId,
				}));
				break;
			}

			const nextEdges = connectedEdges.filter((edge) => (
				edge !== previousEdge
				&& !input.visitedEdges.has(edge)
			));
			if (nextEdges.length !== 1) {
				break;
			}

			const nextEdge = nextEdges[0]!;
			const nextEndpoint = this.resolveOppositeEndpoint(nextEdge, currentEndpoint);
			if (!nextEndpoint) {
				break;
			}

			input.visitedEdges.add(nextEdge);
			edges.push(nextEdge);
			previousEdge = nextEdge;
			currentEndpoint = nextEndpoint;
		}

		return {
			edges,
			diagnostics,
		};
	}

	/**
	 * 解析端点上可参与选择链的相邻边。
	 *
	 * @param endpoint 当前端点。
	 * @param sourceSketchId 来源草图 ID。
	 * @returns 同一来源草图内的可链选边。
	 */
	private resolveConnectedChainEdges(
		endpoint: SketchPointObject,
		sourceSketchId: string,
	): AbstractSketchEdgeObject[] {
		return endpoint.getConnectedEdgesSnapshot()
			.filter((edge): edge is AbstractSketchEdgeObject => (
				this.isSupportedChainEdge(edge)
				&& findAncestorSketchGroupPersistentId(edge) === sourceSketchId
			));
	}

	/**
	 * 解析对象是否为当前来源草图内可链选边。
	 *
	 * @param object 待判断对象。
	 * @param sourceSketchId 来源草图 ID。
	 * @returns 可链选时返回边对象。
	 */
	private resolveSupportedExternalEdge(
		object: Object3D,
		sourceSketchId: string,
	): AbstractSketchEdgeObject | null {
		if (!this.isSupportedChainEdge(object)) {
			return null;
		}

		return findAncestorSketchGroupPersistentId(object) === sourceSketchId
			? object
			: null;
	}

	/**
	 * 判断对象是否为支持链扩展的草图曲线。
	 *
	 * @param object 待判断对象。
	 * @returns 支持链扩展时返回 true。
	 */
	private isSupportedChainEdge(object: unknown): object is AbstractSketchEdgeObject {
		return isAbstractSketchEdgeObject(object);
	}

	/**
	 * 读取曲线的端点。
	 *
	 * @param edge 草图边对象。
	 * @returns 端点集合；闭合或无端点曲线返回空集合。
	 */
	private resolveEndpointPoints(edge: AbstractSketchEdgeObject): SketchPointObject[] {
		return edge.getRelatedSketchPointsSnapshot()
			.filter((point) => point.isEndPoint)
			.slice(0, 2);
	}

	/**
	 * 解析边上与当前端点相对的另一端点。
	 *
	 * @param edge 当前边。
	 * @param endpoint 当前端点。
	 * @returns 另一端点；无法解析时返回 null。
	 */
	private resolveOppositeEndpoint(
		edge: AbstractSketchEdgeObject,
		endpoint: SketchPointObject,
	): SketchPointObject | null {
		return this.resolveEndpointPoints(edge).find((candidate) => candidate !== endpoint) ?? null;
	}

	/**
	 * 创建链展开后的单条来源。
	 *
	 * @param sourceSketchId 来源草图 ID。
	 * @param edge 草图边对象。
	 * @returns UIBridge 可消费的候选来源。
	 */
	private createSource(
		sourceSketchId: string,
		edge: AbstractSketchEdgeObject,
	): SketchEntityReferenceSelectionCandidateSource {
		return {
			descriptor: {
				kind: 'sketch-entity',
				sourceSketchId,
				entityId: edge.persistentId,
			},
			displayLabel: resolveSourceDisplayLabel(edge.name ?? ''),
			sourceObject: edge,
		};
	}
}
